import {
  DEFAULT_SIGNAL,
  parseAddress,
  parseTag,
  resolveSnapshot,
} from "@/features/machines/addressing";
import { TELEMETRY_GAP_GRACE_MS } from "@ghm/domain";
import { evaluateCalc, resolveSignalValue } from "@/features/machines/calc";
import type { Address, CalcField, Machine } from "@/features/machines/types";
import type { LatestPort } from "@/features/devices/types";
import type {
  DashboardWidget,
  DataQuality,
  ResolvedWidgetValue,
  SignalDiagnostic,
  WidgetThreshold,
  WidgetTone,
} from "./types";
import { widgetSources } from "./types";
import { getAgeMs } from "@/features/devices/adapters";
import { resolveAnalogDiagnostic } from "@/features/devices/analogDiagnostic";
import { resolveModbusDiagnostic } from "@/features/devices/modbusDiagnostic";

/** Janela de frescor padrão quando o `/meta` não declara cadência. */
export const DEFAULT_SIGNAL_MAX_AGE_MS = 30_000;
/** Alias retrocompatível para imports antigos. */
export const WIDGET_SIGNAL_MAX_AGE_MS = DEFAULT_SIGNAL_MAX_AGE_MS;

/**
 * Ciclos de telemetria tolerados antes de marcar "desatualizado".
 * Conservador (10×): a 1 Hz, 10s de silêncio. Tolera jitter e perdas de
 * QoS 0 sem flapar, mas não deixa valor velho passar por "ao vivo".
 */
export const STALE_TOLERANCE_CYCLES = 10;
const STALE_FLOOR_MS = TELEMETRY_GAP_GRACE_MS;
const STALE_CEILING_MS = 120_000;

/**
 * Janela de frescor derivada da cadência declarada pelo device no `/meta`
 * (`cadence.telemetryMs`). Hardware como fonte da verdade: o device diz com
 * que frequência publica e toleramos STALE_TOLERANCE_CYCLES perdas. Sem
 * cadência válida, cai no default. Resultado clampado em [10s, 120s].
 */
export function resolveStaleAfterMs(telemetryMs?: number | null): number {
  if (!telemetryMs || !Number.isFinite(telemetryMs) || telemetryMs <= 0) {
    return DEFAULT_SIGNAL_MAX_AGE_MS;
  }
  const derived = telemetryMs * STALE_TOLERANCE_CYCLES;
  return Math.min(STALE_CEILING_MS, Math.max(STALE_FLOOR_MS, derived));
}

/** Estado de runtime de um slot: online agora + janela de frescor do device. */
export interface SlotRuntime {
  online: boolean;
  staleAfterMs: number;
}

/** Mapa slotIndex → runtime. Vem da página (status + lastSeenAt + cadência). */
export type SlotRuntimeBySlot = ReadonlyMap<number, SlotRuntime>;

const EMPTY_SLOT_RUNTIME: SlotRuntimeBySlot = new Map();

/**
 * Fonte ÚNICA de verdade do estado de confiança (fase P0). Decide entre
 * live/stale/waiting/offline a partir do frescor da leitura, da janela de
 * frescor do device (`staleAfterMs`, derivada da cadência) e de o device
 * estar online. Isolado e puro para ser testável e reusado em todo widget.
 */
export function deriveDataQuality(
  measuredAt: string | null | undefined,
  deviceOnline: boolean,
  staleAfterMs: number = DEFAULT_SIGNAL_MAX_AGE_MS,
  now: Date = new Date(),
): DataQuality {
  const ageMs = getAgeMs(measuredAt, now);
  // Sem `measuredAt` = nunca chegou leitura para este sinal. Distingue
  // "device ligado, ainda sem telemetria" de "device caído" — o cerne da
  // confusão que o operador relatou.
  if (ageMs === null) return deviceOnline ? "waiting" : "offline";
  return ageMs <= staleAfterMs ? "live" : "stale";
}

// Ranque para combinar a qualidade de várias entradas (cálculos): o elo
// mais fraco define o todo — um cálculo só é confiável se TODAS as entradas
// forem confiáveis.
const QUALITY_RANK: Record<DataQuality, number> = {
  offline: 0,
  waiting: 1,
  stale: 2,
  live: 3,
};

function weakestQuality(qualities: DataQuality[]): DataQuality {
  if (qualities.length === 0) return "offline";
  return qualities.reduce((weakest, current) =>
    QUALITY_RANK[current] < QUALITY_RANK[weakest] ? current : weakest,
  );
}

/**
 * Aceita as duas notacoes:
 *  - tag publica `G{n}_P{n}_{v|acc|s}` (e legado `S{n}.P{n}` com sinal
 *    implicito `acc`) -> endereco de canal real.
 *  - `V{n}` -> campo calculado derivado.
 */
function parseWidgetSource(source: string): Address | null {
  const tag = parseTag(source);
  if (tag) return tag;
  return parseAddress(source);
}

/**
 * Resolve TODAS as fontes do widget, âncora primeiro. Fonte que não resolve
 * (tag inválida, slot ausente) é omitida da lista, e não vira `null` no meio
 * do array — quem consome quer as leituras que existem, e o agregado conta
 * quantas são para dizer se o card está parcialmente cego.
 *
 * Widget de fonte única devolve um array de um elemento, então o consumidor
 * multi-fonte não precisa de dois caminhos.
 */
export function resolveWidgetValues(
  widget: DashboardWidget,
  machine: Machine,
  latestPorts: LatestPort[],
  calcFields: CalcField[],
  slotRuntime: SlotRuntimeBySlot = EMPTY_SLOT_RUNTIME,
  now: Date = new Date(),
): ResolvedWidgetValue[] {
  return widgetSources(widget).flatMap((source) => {
    const resolved = resolveWidgetValue(
      source === widget.source ? widget : { ...widget, source, extraSources: undefined },
      machine,
      latestPorts,
      calcFields,
      slotRuntime,
      now,
    );
    return resolved ? [resolved] : [];
  });
}

export function resolveWidgetValue(
  widget: DashboardWidget,
  machine: Machine,
  latestPorts: LatestPort[],
  calcFields: CalcField[],
  slotRuntime: SlotRuntimeBySlot = EMPTY_SLOT_RUNTIME,
  // Relogio compartilhado do painel, avancando mesmo com JSON identico ou
  // falha HTTP. Voltar de uma aba suspensa nao torna um snapshot antigo atual.
  now: Date = new Date(),
): ResolvedWidgetValue | null {
  const address = parseWidgetSource(widget.source);
  if (!address) return null;

  if (address.kind === "real") {
    const snapshot = resolveSnapshot(address, machine, latestPorts);
    const ctx = slotRuntime.get(address.slotIndex);
    const staleAfterMs = ctx?.staleAfterMs ?? DEFAULT_SIGNAL_MAX_AGE_MS;
    const deviceOnline = ctx?.online ?? false;
    // Disponibilidade (mostrar o valor) e qualidade usam a MESMA janela por
    // slot — assim "live" e "valor visível" nunca discordam.
    const availableSnapshot = isSnapshotAvailable(snapshot, staleAfterMs, now)
      ? snapshot
      : undefined;
    const signal = address.signal ?? DEFAULT_SIGNAL;
    let numericValue = resolveSignalValue(availableSnapshot, signal);
    let rawValue = sanitizeRawValue(
      signal === "s"
        ? availableSnapshot?.s
        : signal === "v"
          ? availableSnapshot?.v
          : availableSnapshot?.acc,
    );
    const quality = deriveDataQuality(snapshot?.measuredAt, deviceOnline, staleAfterMs, now);
    const analogDiagnostic = signal === "s"
      ? null
      : resolveAnalogDiagnostic(availableSnapshot?.mode, availableSnapshot?.s);
    const modbusDiagnostic = signal === "s"
      ? null
      : toSignalDiagnostic(
          resolveModbusDiagnostic(availableSnapshot?.mode, availableSnapshot?.s),
        );
    const diagnostic = analogDiagnostic ?? modbusDiagnostic;
    // Em falha elétrica, `v` pode ter ficado no latest-port por uma publicação
    // anterior. Nunca apresentar esse valor antigo como leitura atual. O `acc`
    // continua válido (total persistido), porém com diagnóstico de falha.
    const invalidInstantValue =
      signal === "v" &&
      (diagnostic === "fault" || diagnostic?.startsWith("modbus-") === true);
    const modbusValueUnavailable =
      signal === "v" && diagnostic?.startsWith("modbus-") === true;
    if (invalidInstantValue) {
      numericValue = null;
      rawValue = null;
    }
    const thresholdTone = resolveTone(numericValue, widget.settings.thresholds);
    const tone = diagnostic === "fault" || (
      diagnostic?.startsWith("modbus-") && diagnostic !== "modbus-never"
    )
      ? "danger"
      : diagnostic === "under-range" ||
          diagnostic === "over-range" ||
          diagnostic === "modbus-never"
        ? thresholdTone === "danger" ? "danger" : "warn"
        : thresholdTone;
    return {
      source: widget.source,
      address,
      value: rawValue,
      numericValue,
      measuredAt: snapshot?.measuredAt ?? null,
      tone,
      isAvailable: !!availableSnapshot && !modbusValueUnavailable,
      quality,
      ...(diagnostic ? { diagnostic } : {}),
    };
  }

  const calcField = calcFields.find((field) => field.derivedIndex === address.derivedIndex);
  if (!calcField) {
    return {
      source: widget.source,
      address,
      value: null,
      numericValue: null,
      measuredAt: null,
      tone: "neutral",
      isAvailable: false,
      quality: "offline",
    };
  }

  const inputResolutions = calcField.inputs.map((input) => {
    const snapshot = resolveSnapshot(input, machine, latestPorts);
    const ctx = input.kind === "real" ? slotRuntime.get(input.slotIndex) : undefined;
    const staleAfterMs = ctx?.staleAfterMs ?? DEFAULT_SIGNAL_MAX_AGE_MS;
    const deviceOnline = ctx?.online ?? false;
    const availableSnapshot = isSnapshotAvailable(snapshot, staleAfterMs, now)
      ? snapshot
      : undefined;
    const value = resolveSignalValue(availableSnapshot, input.signal ?? DEFAULT_SIGNAL);
    const quality = deriveDataQuality(snapshot?.measuredAt, deviceOnline, staleAfterMs, now);
    return { snapshot, availableSnapshot, value, quality };
  });
  const numericValue = evaluateCalc(
    calcField.op,
    inputResolutions.map((entry) => entry.value),
  );
  const measuredAt =
    inputResolutions
      .map((entry) => entry.snapshot?.measuredAt ?? null)
      .filter((value): value is string => !!value)
      .sort((left, right) => right.localeCompare(left))[0] ?? null;
  const allInputsAvailable = inputResolutions.every((entry) => !!entry.availableSnapshot);
  // Qualidade do cálculo = elo mais fraco das entradas.
  const quality = weakestQuality(inputResolutions.map((entry) => entry.quality));

  return {
    source: widget.source,
    address,
    value: numericValue,
    numericValue,
    measuredAt,
    tone: resolveTone(numericValue, widget.settings.thresholds),
    isAvailable: allInputsAvailable && numericValue !== null,
    quality,
  };
}

function toSignalDiagnostic(
  diagnostic: ReturnType<typeof resolveModbusDiagnostic>,
): SignalDiagnostic | null {
  return diagnostic ? `modbus-${diagnostic}` : null;
}

export function resolveTone(
  value: number | null,
  thresholds: DashboardWidget["settings"]["thresholds"],
): WidgetTone {
  return resolveThreshold(value, thresholds)?.tone ?? "neutral";
}

/** Mesmo limite governa a cor e sua explicação; não altera a ordem salva. */
export function resolveThreshold(
  value: number | null,
  thresholds: DashboardWidget["settings"]["thresholds"],
): WidgetThreshold | null {
  if (value === null || !Number.isFinite(value) || !thresholds?.length) return null;

  return (
    thresholds
      .filter((threshold) =>
        threshold.direction === "below" ? value <= threshold.at : value >= threshold.at,
      )
      .sort((left, right) => {
        const byTone = toneSeverity(right.tone) - toneSeverity(left.tone);
        if (byTone !== 0) return byTone;
        if (left.direction === "below" || right.direction === "below") {
          return left.at - right.at;
        }
        return right.at - left.at;
      })[0] ?? null
  );
}

function toneSeverity(tone: Exclude<WidgetTone, "neutral">): number {
  if (tone === "danger") return 4;
  if (tone === "warn") return 3;
  if (tone === "info") return 2;
  return 1;
}

/**
 * Sanitiza o valor cru exibido (`value.value`). Números não-finitos
 * (NaN/±Infinity) viram `null` para nunca propagarem como "valor"; booleanos
 * e strings (usados pelo LED) passam intactos. `undefined` vira `null`.
 */
function sanitizeRawValue(raw: unknown): unknown {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  return raw ?? null;
}

function isSnapshotAvailable(
  snapshot: LatestPort | undefined,
  maxAgeMs: number = DEFAULT_SIGNAL_MAX_AGE_MS,
  now: Date = new Date(),
): snapshot is LatestPort {
  if (!snapshot) return false;
  const ageMs = getAgeMs(snapshot.measuredAt, now);
  return ageMs !== null && ageMs <= maxAgeMs;
}
